import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gauge, Package, Plus, Trash2, Save, Coins } from "lucide-react";
import { toast } from "sonner";

const chf = (v) => Number(v || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PackagesEditor({ packages, onSave }) {
  const [rows, setRows] = useState(packages);
  useEffect(() => setRows(packages), [packages]);
  const upd = (i, k, v) => setRows((r) => r.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  return (
    <div className="rounded-md border border-slate-200 p-3 mb-4" data-testid="ai-packages-editor">
      <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700 mb-2"><Package className="w-3.5 h-3.5" /> KI-Credit-Pakete (kauft der Mandant per Karte, Gutschrift sofort nach Zahlung)</div>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-slate-500"><th>ID</th><th>Name</th><th>Credits CHF</th><th>Preis CHF</th><th>Aktiv</th><th></th></tr></thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={i} data-testid={`ai-package-row-${i}`}>
              <td className="pr-1"><Input className="h-7 text-xs" value={p.id} onChange={(e) => upd(i, "id", e.target.value)} data-testid={`ai-package-id-${i}`} /></td>
              <td className="pr-1"><Input className="h-7 text-xs" value={p.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`ai-package-name-${i}`} /></td>
              <td className="pr-1"><Input className="h-7 text-xs w-24" type="number" value={p.credits_chf} onChange={(e) => upd(i, "credits_chf", Number(e.target.value))} data-testid={`ai-package-credits-${i}`} /></td>
              <td className="pr-1"><Input className="h-7 text-xs w-24" type="number" value={p.price_chf} onChange={(e) => upd(i, "price_chf", Number(e.target.value))} data-testid={`ai-package-price-${i}`} /></td>
              <td><input type="checkbox" checked={p.active !== false} onChange={(e) => upd(i, "active", e.target.checked)} data-testid={`ai-package-active-${i}`} /></td>
              <td><button className="text-slate-400 hover:text-rose-600" onClick={() => setRows((r) => r.filter((_, j) => j !== i))} data-testid={`ai-package-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-2 mt-2">
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setRows((r) => [...r, { id: `paket-${r.length + 1}`, name: "Neues Paket", credits_chf: 25, price_chf: 25, active: true }])} data-testid="ai-package-add"><Plus className="w-3 h-3 mr-1" />Paket</Button>
        <Button size="sm" className="h-7 text-xs" onClick={() => onSave(rows)} data-testid="ai-packages-save"><Save className="w-3 h-3 mr-1" />Pakete speichern</Button>
      </div>
    </div>
  );
}

function CompanyRow({ c, onSaved }) {
  const [budget, setBudget] = useState(c.budget_chf ?? "");
  const [mode, setMode] = useState(c.mode || "warn");
  const [grant, setGrant] = useState("");
  const save = async () => {
    try {
      await apiClient.put(`/admin/ai-budget/${c.company_id}`, { budget_chf: budget === "" ? null : Number(budget), mode });
      toast.success(`Budget für ${c.name} gespeichert`); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const doGrant = async () => {
    if (!grant || Number(grant) <= 0) return;
    try {
      await apiClient.post(`/admin/ai-credits/${c.company_id}/grant`, { amount_chf: Number(grant), note: "Manuelle Gutschrift (Super-Admin)" });
      toast.success(`CHF ${chf(grant)} Credits gutgeschrieben`); setGrant(""); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  const pctColor = c.blocked ? "text-rose-700 font-semibold" : c.pct != null && c.pct >= 80 ? "text-amber-700" : "text-slate-600";
  return (
    <tr className="border-b border-slate-50" data-testid={`ai-budget-row-${c.company_id}`}>
      <td className="py-1 text-slate-800">{c.name}{c.blocked && <span className="ml-1 text-[10px] text-rose-700" data-testid={`ai-budget-blocked-${c.company_id}`}>pausiert</span>}</td>
      <td className={`text-right ${pctColor}`}>{chf(c.used_chf)}{c.pct != null ? ` (${c.pct} %)` : ""}</td>
      <td><Input className="h-7 w-24 text-xs" type="number" placeholder="kein Deckel" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid={`ai-budget-input-${c.company_id}`} /></td>
      <td>
        <select className="h-7 text-xs border border-slate-200 rounded px-1" value={mode} onChange={(e) => setMode(e.target.value)} data-testid={`ai-budget-mode-${c.company_id}`}>
          <option value="warn">warnen</option><option value="pause">pausieren</option>
        </select>
      </td>
      <td><Button size="sm" variant="outline" className="h-7 text-xs" onClick={save} data-testid={`ai-budget-save-${c.company_id}`}><Save className="w-3 h-3" /></Button></td>
      <td className="text-right font-medium" data-testid={`ai-credits-balance-${c.company_id}`}>{chf(c.credits_chf)}</td>
      <td className="whitespace-nowrap"><Input className="h-7 w-20 text-xs inline-block mr-1" type="number" placeholder="CHF" value={grant} onChange={(e) => setGrant(e.target.value)} data-testid={`ai-grant-input-${c.company_id}`} /><Button size="sm" variant="ghost" className="h-7 text-xs" onClick={doGrant} data-testid={`ai-grant-save-${c.company_id}`}><Coins className="w-3 h-3 mr-1" />gutschreiben</Button></td>
    </tr>
  );
}

export default function AdminAiBillingCard() {
  const [data, setData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [filter, setFilter] = useState("");
  const load = async () => {
    try {
      const [b, c] = await Promise.all([apiClient.get("/admin/ai-billing"), apiClient.get("/admin/companies")]);
      setData(b.data); setCompanies(c.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const saveBilling = async (body) => {
    try { const r = await apiClient.put("/admin/ai-billing", body); setData(r.data); toast.success("Gespeichert"); window.dispatchEvent(new Event("ai-billing-changed")); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };
  if (!data) return null;
  const rows = data.companies.filter((c) => !filter || c.name.toLowerCase().includes(filter.toLowerCase()));
  return (
    <Card className="p-5 border border-slate-200 bg-white shadow-sm mt-4" data-testid="admin-ai-billing-card">
      <div className="flex items-start gap-3 mb-4">
        <Gauge className="w-5 h-5 text-slate-700 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900">KI-Budget, Credits &amp; Verrechnung je Mandant</h3>
          <p className="text-xs text-slate-500">Monatlicher Deckel pro Mandant (inkl. Aufschlag {data.markup_pct} %). «warnen» zeigt dem Mandanten Hinweise, «pausieren» stoppt den Assistenten, sobald Budget und Credits aufgebraucht sind – dann kauft der Mandant Credits.</p>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-4 text-xs" data-testid="ai-billing-company-section">
        <div className="min-w-[280px]">
          <label className="text-[11px] text-slate-500">Betreiber-Firma (Rechnungen für KI-Positionen werden hier als Entwurf angelegt)</label>
          <select className="h-8 w-full text-xs border border-slate-200 rounded px-2" value={data.billing_company_id || ""} onChange={(e) => saveBilling({ billing_company_id: e.target.value || "" })} data-testid="ai-billing-company-select">
            <option value="">— nicht gewählt —</option>
            {companies.map((c) => <option key={c._id || c.id} value={c._id || c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <PackagesEditor packages={data.packages} onSave={(pk) => saveBilling({ packages: pk })} />
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-medium text-slate-700">Mandanten · Verbrauch {data.year_month}</div>
        <Input className="h-7 w-48 text-xs" placeholder="Mandant suchen…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="ai-budget-filter" />
      </div>
      <table className="w-full text-xs" data-testid="ai-budget-table">
        <thead><tr className="text-left text-slate-500 border-b border-slate-100"><th className="py-1">Mandant</th><th className="text-right">Verbrauch CHF</th><th>Budget/Monat</th><th>Bei Erreichen</th><th></th><th className="text-right">Credits CHF</th><th>Gutschrift</th></tr></thead>
        <tbody>{rows.map((c) => <CompanyRow key={c.company_id} c={c} onSaved={load} />)}</tbody>
      </table>
    </Card>
  );
}
